import { Activity, ReactElement, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MuiBreadcrumbs, { BreadcrumbsProps } from '@mui/material/Breadcrumbs';
import Box from '@mui/material/Box';

// third party
import { useTranslation } from 'react-i18next';

// project imports
import { ThemeDirection } from 'config';
import { useMenuItems } from 'menu-items';
import useConfig from 'hooks/useConfig';

// assets
import { IconChevronRight, IconTallymark1 } from '@tabler/icons-react';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import HomeIcon from '@mui/icons-material/Home';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';

// types
import { NavItemType, OverrideIcon } from 'types';

interface BreadcrumbLinkProps {
  title: string;
  to?: string;
  icon?: string | OverrideIcon;
}

// ==============================|| BREADCRUMBS TITLE ||============================== //

function BTitle({ title }: { title: string }) {
  const { t } = useTranslation();

  return (
    <Grid>
      <Typography variant="h4" sx={{ fontWeight: 500 }}>
        {t(title)}
      </Typography>
    </Grid>
  );
}

// ==============================|| BREADCRUMBS ||============================== //

interface Props {
  card?: boolean;
  custom?: boolean;
  divider?: boolean;
  heading?: string;
  icon?: boolean;
  icons?: boolean;
  links?: BreadcrumbLinkProps[];
  maxItems?: number;
  rightAlign?: boolean;
  separator?: OverrideIcon;
  title?: boolean;
  titleBottom?: boolean;
  sx?: BreadcrumbsProps['sx'];
}

export default function Breadcrumbs({
  card,
  custom = false,
  divider = false,
  heading,
  icon = true,
  icons,
  links,
  maxItems,
  rightAlign = true,
  separator = IconChevronRight,
  title = true,
  titleBottom,
  sx,
  ...others
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const {
    state: { themeDirection }
  } = useConfig();

  const navigation = useMenuItems();

  const iconSX = {
    marginRight: 6,
    marginTop: -2,
    width: '1rem',
    height: '1rem',
    color: theme.vars.palette.secondary.main
  };

  const linkSX = {
    display: 'flex',
    textDecoration: 'none',
    alignContent: 'center',
    alignItems: 'center'
  };

  let customLocation = location.pathname;

  const { main, item } = useMemo(() => {
    let activeMain: NavItemType | undefined;
    let activeItem: NavItemType | undefined;

    const getCollapse = (menu: NavItemType) => {
      if (!custom && menu.children) {
        menu.children.forEach((collapse: NavItemType) => {
          if (collapse.type && collapse.type === 'collapse') {
            getCollapse(collapse as { children: NavItemType[]; type?: string });
            if (collapse.url === customLocation) {
              activeMain = collapse;
              activeItem = collapse;
            }
          } else if (collapse.type && collapse.type === 'item') {
            if (customLocation === collapse.url) {
              activeMain = menu;
              activeItem = collapse;
            }
          }
        });
      }
    };

    navigation?.items?.forEach((menu: NavItemType) => {
      if (menu.type && menu.type === 'group') {
        if (menu?.url && menu.url === customLocation) {
          activeMain = menu;
          activeItem = menu;
        } else {
          getCollapse(menu as { children: NavItemType[]; type?: string });
        }
      }
    });

    return { main: activeMain, item: activeItem };
  }, [custom, customLocation, navigation]);

  // item separator
  const SeparatorIcon = separator!;
  const separatorIcon = separator ? <SeparatorIcon stroke={1.5} size="16px" /> : <IconTallymark1 stroke={1.5} size="16px" />;

  let mainContent;
  let itemContent;
  let breadcrumbContent: ReactElement = <Typography />;
  let itemTitle: NavItemType['title'] = '';
  let CollapseIcon;
  let ItemIcon;

  // collapse item
  if (main && main.type === 'collapse') {
    CollapseIcon = main.icon ? main.icon : AccountTreeTwoToneIcon;
    mainContent = (
      <Typography
        {...(main.url && { component: Link, to: main.url })}
        variant="h6"
        noWrap
        sx={{
          overflow: 'hidden',
          lineHeight: 1.5,
          mb: -0.625,
          textOverflow: 'ellipsis',
          maxWidth: { xs: 102, sm: 'unset' },
          display: 'inline-block'
        }}
        color={window.location.pathname === main.url ? 'text.primary' : 'text.secondary'}
      >
        <Activity mode={icons ? 'visible' : 'hidden'}>
          <CollapseIcon style={{ ...iconSX, ...(themeDirection === ThemeDirection.RTL && { marginLeft: 6, marginRight: 0 }) }} />
        </Activity>
        {t(main.title as string)}
      </Typography>
    );
  }

  if (!custom && main && main.type === 'collapse' && main.breadcrumbs === true) {
    breadcrumbContent = (
      <Card sx={card === false ? { mb: 3, bgcolor: 'transparent', ...sx } : { mb: 3, bgcolor: 'background.default', ...sx }} {...others}>
        <Box sx={{ p: 1.25, px: card === false ? 0 : 2 }}>
          <Grid
            container
            direction={rightAlign ? 'row' : 'column'}
            sx={{ justifyContent: rightAlign ? 'space-between' : 'flex-start', alignItems: rightAlign ? 'center' : 'flex-start' }}
            spacing={1}
          >
            <Activity mode={title && !titleBottom ? 'visible' : 'hidden'}>
              <BTitle title={main.title as string} />
            </Activity>
            <Grid>
              <MuiBreadcrumbs
                aria-label="breadcrumb"
                maxItems={maxItems || 8}
                separator={separatorIcon}
                sx={{ '& .MuiBreadcrumbs-separator': { width: 16, ml: 1.25, mr: 1.25 } }}
              >
                <Typography component={Link} to="/" variant="h6" sx={{ ...linkSX, color: 'text.secondary' }}>
                  <Activity mode={icons ? 'visible' : 'hidden'}>
                    <HomeTwoToneIcon style={iconSX} />
                  </Activity>
                  <Activity mode={icon && !icons ? 'visible' : 'hidden'}>
                    <HomeIcon style={{ ...iconSX, marginRight: 0 }} />
                  </Activity>
                  {(!icon || icons) && t('home.title')}
                </Typography>
                {mainContent}
              </MuiBreadcrumbs>
            </Grid>
            <Activity mode={title && titleBottom ? 'visible' : 'hidden'}>
              <BTitle title={main.title as string} />
            </Activity>
          </Grid>
        </Box>
        <Activity mode={card === false && divider !== false ? 'visible' : 'hidden'}>
          <Divider sx={{ mt: 2 }} />
        </Activity>
      </Card>
    );
  }

  // items
  if ((item && item.type === 'item') || (item?.type === 'group' && item?.url) || custom) {
    itemTitle = item?.title;

    ItemIcon = item?.icon ? item.icon : AccountTreeTwoToneIcon;
    itemContent = (
      <Typography
        variant="h6"
        noWrap
        sx={{
          ...linkSX,
          color: 'text.secondary',
          display: 'inline-block',
          overflow: 'hidden',
          lineHeight: 1.5,
          mb: -0.625,
          textOverflow: 'ellipsis',
          maxWidth: { xs: 102, sm: 'unset' }
        }}
      >
        <Activity mode={icons ? 'visible' : 'hidden'}>
          <ItemIcon style={{ ...iconSX, ...(themeDirection === ThemeDirection.RTL && { marginLeft: 6, marginRight: 0 }) }} />
        </Activity>
        {t(itemTitle as string)}
      </Typography>
    );

    let tempContent = (
      <MuiBreadcrumbs
        aria-label="breadcrumb"
        maxItems={maxItems || 8}
        separator={separatorIcon}
        sx={{ '& .MuiBreadcrumbs-separator': { width: 16, mx: 0.75 } }}
      >
        <Typography component={Link} to="/" variant="h6" sx={{ ...linkSX, color: 'text.secondary' }}>
          <Activity mode={icons ? 'visible' : 'hidden'}>
            <HomeTwoToneIcon style={{ ...iconSX, ...(themeDirection === ThemeDirection.RTL && { marginLeft: 6, marginRight: 0 }) }} />
          </Activity>
          <Activity mode={icon && !icons ? 'visible' : 'hidden'}>
            <HomeIcon style={{ ...iconSX, marginRight: 0 }} />
          </Activity>
          {(!icon || icons) && t('home.title')}
        </Typography>
        {mainContent}
        {itemContent}
      </MuiBreadcrumbs>
    );

    if (custom && links && links?.length > 0) {
      tempContent = (
        <MuiBreadcrumbs
          aria-label="breadcrumb"
          maxItems={maxItems || 8}
          separator={separatorIcon}
          sx={{ '& .MuiBreadcrumbs-separator': { width: 16, ml: 1.25, mr: 1.25 } }}
        >
          {links?.map((link: BreadcrumbLinkProps, index: number) => {
            CollapseIcon = link.icon ? link.icon : AccountTreeTwoToneIcon;

            return (
              <Typography
                key={index}
                {...(link.to && { component: Link, to: link.to })}
                variant="h6"
                sx={{ ...linkSX, color: 'text.secondary' }}
              >
                <Activity mode={link.icon ? 'visible' : 'hidden'}>
                  <CollapseIcon style={iconSX} />
                </Activity>
                {t(link.title)}
              </Typography>
            );
          })}
        </MuiBreadcrumbs>
      );
    }

    // main
    if (item?.breadcrumbs !== false || custom) {
      breadcrumbContent = (
        <Card
          sx={
            card === false
              ? { mb: 3, bgcolor: 'transparent', ...sx }
              : {
                  mb: 3,
                  bgcolor: 'background.default',
                  ...theme.applyStyles('dark', { bgcolor: 'dark.main' }),
                  ...sx
                }
          }
          {...others}
        >
          <Box sx={{ p: 1.25, px: card === false ? 0 : 2 }}>
            <Grid
              container
              direction={rightAlign ? 'row' : 'column'}
              sx={{ justifyContent: rightAlign ? 'space-between' : 'flex-start', alignItems: rightAlign ? 'center' : 'flex-start' }}
              spacing={1}
            >
              <Activity mode={title && !titleBottom ? 'visible' : 'hidden'}>
                <BTitle title={custom ? (heading as string) : (item?.title as string)} />
              </Activity>
              <Grid>{tempContent}</Grid>
              <Activity mode={title && titleBottom ? 'visible' : 'hidden'}>
                <BTitle title={custom ? (heading as string) : (item?.title as string)} />
              </Activity>
            </Grid>
          </Box>
          <Activity mode={card === false && divider !== false ? 'visible' : 'hidden'}>
            <Divider sx={{ mt: 2 }} />
          </Activity>
        </Card>
      );
    }
  }

  return breadcrumbContent;
}
